/** Absolute (`scheme://`) or protocol-relative (`//host`) URL. Anything else resolves against the page. */
const ABSOLUTE_URL_PATTERN: RegExp = /^([a-z][a-z\d+\-.]*:)?\/\//i;

/**
 * Returns true when {@link url} should receive platform API auth headers.
 *
 * A URL matches only when it resolves to the platform API origin. An empty
 * {@link platformApiBaseUrl} means the API is same-origin, so relative paths (`/x`, `./x`,
 * `../x`, `x`) match. With a cross-origin API (the global shell on `dtims.app` calling
 * `https://<r>.api.dtims.app`), relative paths are the static host (assets, `/api/*` managed
 * functions) and do not match. A URL that cannot be resolved never matches: credentials are
 * not sent to a host we cannot identify.
 * @param url - The request URL.
 * @param platformApiBaseUrl - The platform API base URL, or '' when the API is same-origin.
 * @param pageOrigin - The current page origin; defaults to `location.origin`.
 * @returns Whether the request targets the platform API.
 */
export function isPlatformApiRequest(
  url: string,
  platformApiBaseUrl: string,
  pageOrigin: string | undefined = globalThis.location?.origin
): boolean {
  const isRelative: boolean = !url || !ABSOLUTE_URL_PATTERN.test(url);
  if (isRelative && !platformApiBaseUrl) {
    return true;
  }

  try {
    const requestOrigin: string = new URL(url || '/', pageOrigin).origin;
    const platformOrigin: string = new URL(platformApiBaseUrl || (pageOrigin ?? '')).origin;
    return requestOrigin === platformOrigin;
  } catch {
    return false;
  }
}
